import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { PricingModelV2Dto } from '@/app/common/general-types/pricing.dto';
import { PricingModelV2Service } from '@/app/services/pricing-model-v2/pricing-model-v2.service';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { take } from 'rxjs';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { FormatService } from '@/app/common/format/format.service';
import { RouterLink } from '@angular/router';
import { AlertService } from '@/app/atoms/alert/alert.service';

@Component({
  selector: 'gmo-pricing-models',
  imports: [DatePipe, CurrencyPipe, RouterLink],
  templateUrl: './pricing-models.html',
  styleUrl: './pricing-models.scss',
})
export class PricingModels implements OnInit {

  /** Full list from API (includes deleted and non-deleted). */
  modelsSource = signal<PricingModelV2Dto[] | undefined>(undefined);

  /** 'active' = not deleted (default); 'deleted' = soft-deleted only. */
  deletedListTab = signal<'active' | 'deleted'>('active');

  selectedModel = signal<PricingModelV2Dto | undefined>(undefined);

  searchQuery = signal<string>('');

  readonly modelsForCurrentTab = computed(() => {
    const src = this.modelsSource();
    if (!src) {
      return undefined;
    }
    const showDeleted = this.deletedListTab() === 'deleted';
    return src.filter((m) => (showDeleted ? Boolean(m.isDeleted) : !m.isDeleted));
  });

  readonly displayPricingModels = computed(() => {
    const base = this.modelsForCurrentTab();
    if (!base) {
      return undefined;
    }
    const query = this.searchQuery().trim().toLowerCase();
    if (query.length === 0) {
      return base;
    }
    return base.filter((model) => model.title.toLowerCase().includes(query));
  });

  /** Services */
  private pricingModelService = inject(PricingModelV2Service);
  public formatService = inject(FormatService);
  public alertService = inject(AlertService);
  public toastNotificationService = inject(ToastNotificationService);
  /** Services - END */

  ngOnInit(): void {
    this.getPricingModels();
  }

  getPricingModels() {
    this.pricingModelService.getAllPricingModels().pipe(take(1)).subscribe((res) => {
      console.log('Pricing Models: ', res);
      this.modelsSource.set(res);
    }, error => {
      console.error('Error getting pricing models: ', error);
      this.modelsSource.set([]);
    });
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
  }

  getListFilterTabClass(tab: 'active' | 'deleted'): string {
    return this.deletedListTab() === tab
      ? 'rounded-md px-3 py-2 text-sm font-medium bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'rounded-md px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer';
  }

  onListFilterTabChange(tab: 'active' | 'deleted'): void {
    this.deletedListTab.set(tab);
    const selected = this.selectedModel();
    if (!selected) {
      return;
    }
    const wantDeleted = tab === 'deleted';
    const selectedIsDeleted = Boolean(selected.isDeleted);
    if (wantDeleted !== selectedIsDeleted) {
      this.selectedModel.set(undefined);
    }
  }

  isSelectedModel(model: PricingModelV2Dto): boolean {
    return this.selectedModel()?.id === model.id;
  }

  onModelSelect(model: PricingModelV2Dto): void {
    console.log('👀👀👀 onModelSelect() --> model: ', model);
    this.selectedModel.set(model);
  }

  setToCurrentModel() {
    console.log('👀👀👀 setToCurrentModel()');
  }

  async deleteModel() {
    console.log('👀👀👀 deleteModel()');
    if (!this.selectedModel()?.id) {
      return;
    }
    const confirmed = await this.alertService.confirm({
      title: 'Are you sure?',
      message: 'Are you sure you want to delete this pricing model?',
      type: 'confirm',
      icon: 'warning',
      confirmText: 'Yes',
      cancelText: 'No',
    });

    if (!confirmed) {
      return;
    }

    this.pricingModelService.deletePricingModel(this.selectedModel()!.id).pipe(take(1)).subscribe((res) => {
      console.log('👀👀👀 deleteModel() --> res: ', res);
      this.getPricingModels();
      this.selectedModel.set(undefined);
      this.toastNotificationService.alert({
        title: 'Pricing model deleted',
        message: 'The pricing model has been deleted successfully.',
        type: 'success',
        icon: 'success',
      });
    }, error => {
      console.error('👀👀👀 deleteModel() --> error: ', error);
      this.toastNotificationService.alert({
        title: 'Error',
        message: 'Could not delete pricing model. Please try again.',
        type: 'error',
        icon: 'error',
      });
    });
  }
}
